import { Link } from '@tanstack/react-router';
import type { BlogPost } from '@/types/blog';
import { categoryLabels } from '@/types/blog';

interface BlogCardProps {
  post: BlogPost;
  readingTime: number;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

export function BlogCard({ post, readingTime }: BlogCardProps) {
  const { title, description, publishDate, category, image, author } = post.frontmatter;

  return (
    <article className="group bg-white rounded-2xl border border-neutral-200 hover:border-gold-300 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
      <Link to="/blog/$slug" params={{ slug: post.slug }} className="block">
        {/* Image */}
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={image.src}
            alt={image.alt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Category Badge */}
          <span className="inline-block text-gold-600 font-semibold text-xs uppercase tracking-wider mb-3">
            {categoryLabels[category]}
          </span>

          {/* Title */}
          <h3 className="text-xl font-bold text-navy-900 mb-2 group-hover:text-gold-600 transition-colors line-clamp-2">
            {title}
          </h3>

          {/* Description */}
          <p className="text-neutral-600 text-sm mb-4 line-clamp-2">
            {description}
          </p>

          {/* Meta */}
          <div className="flex items-center justify-between text-sm text-neutral-500">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-gold-100 flex items-center justify-center">
                <span className="text-gold-700 font-semibold text-xs">
                  {author.name.charAt(0)}
                </span>
              </div>
              <span>{author.name}</span>
            </div>
            <div className="flex items-center gap-3">
              <time dateTime={publishDate}>
                {formatDate(publishDate)}
              </time>
              <span className="text-neutral-300">•</span>
              <span>{readingTime} min</span>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
}
